/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, Heading as HeadingCore, type HeadingProps as HeadingCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface HeadingProps extends Omit<HeadingCoreProps, 'style' | 'className'> {
  /** The size variant of the heading. (Default: 16) */
  size?: keyof typeof sizeVariants,
  /** The color variant of the heading. (Default: primary) */
  color?: keyof typeof colorVariants,
  /** The weight variant of the heading. (Default: regular) */
  weight?: keyof typeof weightVariants,
  style?: stylex.StyleXStyles;
}

const HeadingContext = createContext<ContextValue<HeadingProps, HTMLHeadingElement>>({});

const Heading = forwardRef((props: HeadingProps, ref: ForwardedRef<HTMLHeadingElement>) => {
  [props, ref] = useContextProps(props, ref, HeadingContext);

  const { style, children, size = 16, color = 'primary', weight = 'regular', ...coreProps } = props;

  return (
    <HeadingCore
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        colorVariants[color],
        weightVariants[weight],
        letterSpacingVariants[`${weight}${size.toString()}` as keyof typeof letterSpacingVariants],
        style,
      )}
      {...coreProps}>
      {children}
    </HeadingCore>
  );
});

const styles = stylex.create({
  base: {
    fontFamily: 'BasierCircle',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    margin: 0,
  },
});

const sizeVariants = stylex.create({
  72: {
    fontSize: 72,
    lineHeight: '78px'
  },
  64: {
    fontSize: 64,
    lineHeight: '70px'
  },
  52: {
    fontSize: 52,
    lineHeight: '60px'
  },
  48: {
    fontSize: 48,
    lineHeight: '54px'
  },
  40: {
    fontSize: 40,
    lineHeight: '46px'
  },
  34: {
    fontSize: 34,
    lineHeight: '40px'
  },
  28: {
    fontSize: 28,
    lineHeight: '34px'
  },
  24: {
    fontSize: 24,
    lineHeight: '30px'
  },
  20: {
    fontSize: 20,
    lineHeight: '24px'
  },
  18: {
    fontSize: 18,
    lineHeight: '22px'
  },
  16: {
    fontSize: 16,
    lineHeight: '20px'
  },
});

const colorVariants = stylex.create({
  primary: { color: $.textPrimary },
  secondary: { color: $.textSecondary },
  tertiary: { color: $.textTertiary },
  quaternary: { color: $.textQuaternary },
  placeholder: { color: $.textPlaceholder },
  disabled: { color: $.textDisabled },
  inverted: { color: $.textInverted },
  interactive: { color: $.textInteractive },
  interactiveHover: { color: $.textInteractiveHover },
  positive: { color: $.textPositive },
  positiveHover: { color: $.textPositiveHover },
  critical: { color: $.textCritical },
  criticalHover: { color: $.textCriticalHover },
  white: { color: $.textWhite },
  black: { color: $.textBlack },
});

const weightVariants = stylex.create({
  semibold: { fontWeight: 600 },
  medium: { fontWeight: 500 },
  regular: { fontWeight: 400 },
});

const letterSpacingVariants = stylex.create({
  semibold72: { letterSpacing: -1.44 },
  medium72: { letterSpacing: -1.872 },
  regular72: { letterSpacing: -2.88 },
  semibold64: { letterSpacing: -1.2 },
  medium64: { letterSpacing: -1.4 },
  regular64: { letterSpacing: -2.2 },
  semibold52: { letterSpacing: -0.78 },
  medium52: { letterSpacing: -0.78 },
  regular52: { letterSpacing: -2.34 },
  semibold48: { letterSpacing: -0.72 },
  medium48: { letterSpacing: -0.72 },
  regular48: { letterSpacing: -1.44 },
  semibold40: { letterSpacing: -0.6 },
  medium40: { letterSpacing: -0.6 },
  regular40: { letterSpacing: -1.6 },
  semibold34: { letterSpacing: -0.476 },
  medium34: { letterSpacing: -0.476 },
  regular34: { letterSpacing: -1.02 },
  semibold28: { letterSpacing: -0.392 },
  medium28: { letterSpacing: -0.392 },
  regular28: { letterSpacing: -0.56 },
  semibold24: { letterSpacing: -0.24 },
  medium24: { letterSpacing: -0.24 },
  regular24: { letterSpacing: -0.72 },
  semibold20: { letterSpacing: -0.2 },
  medium20: { letterSpacing: -0.5 },
  regular20: { letterSpacing: -0.5 },
  semibold18: { letterSpacing: -0.18 },
  medium18: { letterSpacing: -0.45 },
  regular18: { letterSpacing: -0.45 },
  semibold16: { letterSpacing: -0.048 },
  medium16: { letterSpacing: -0.224 },
  regular16: { letterSpacing: -0.256 },
});

export { Heading, HeadingContext, type HeadingProps };
